import { useQuery } from "@tanstack/react-query";
import { API_BASE_URL } from "@/lib/env";
import { extractTenantSubdomain } from "@/lib/tenantFromHost";

/**
 * Runtime tenant info resolved from the current hostname via
 * `GET /api/v1/tenants/by-subdomain/{subdomain}`. Mirrors the backend
 * contract (Phase C). `logo_url` is reserved for a future hosted-logo
 * pass and is not consumed today — branding still uses bundled assets
 * keyed by `branding.partner_key`.
 */
export interface TenantInfo {
  subdomain: string;
  name: string;
  branding: {
    partner_key: string;
    brand_color?: string;
    logo_url?: string;
  };
}

const FIVE_MINUTES = 5 * 60_000;

async function fetchTenant(subdomain: string): Promise<TenantInfo | null> {
  try {
    const res = await fetch(
      `${API_BASE_URL}/api/v1/tenants/by-subdomain/${encodeURIComponent(subdomain)}`,
      { method: "GET", credentials: "omit" },
    );
    if (!res.ok) return null;
    // Backend wraps the payload as `{ tenant: { ... } }` per
    // BACKEND_INTEGRATION.md §23.2. Unwrap before returning so consumers
    // can access `.branding.partner_key` directly. Without this unwrap,
    // CoBrandedHeader reads `data.branding` which is `undefined` and
    // every tenant falls back to generic chrome (no partner pill) — the
    // bug observed on rockwell.uat.digavouch.com 2026-06-08.
    const body = (await res.json()) as { tenant?: TenantInfo } | null;
    return body?.tenant ?? null;
  } catch {
    // Defensive: branding must never break the app.
    return null;
  }
}

/**
 * `useTenant` resolves tenant branding from `window.location.hostname`.
 *
 * - Generic hosts (app.*, localhost, *.netlify.app, *.lovable.app, …)
 *   short-circuit to `{ tenant: null, loading: false }` and never hit
 *   the network.
 * - Tenant hosts (`<sub>.digavouch.com` etc.) issue exactly one cached
 *   request per page load; `staleTime` matches backend `Cache-Control`.
 * - Any non-2xx (incl. 404) or thrown error resolves to `null` — the
 *   caller falls back to generic chrome.
 */
export function useTenant(): { tenant: TenantInfo | null; loading: boolean } {
  const subdomain =
    typeof window !== "undefined" ? extractTenantSubdomain(window.location.hostname) : null;

  const query = useQuery({
    queryKey: ["tenant", subdomain],
    queryFn: () => fetchTenant(subdomain as string),
    enabled: !!subdomain,
    staleTime: FIVE_MINUTES,
    gcTime: FIVE_MINUTES,
    retry: false,
    refetchOnWindowFocus: false,
  });

  if (!subdomain) return { tenant: null, loading: false };
  return { tenant: query.data ?? null, loading: query.isLoading };
}
